// Describing a new file with a vocabulary someone already publishes
// (docs/ref-belonging-plan.md, step 5.5): DP picks REF's belonging terms, each
// column is matched to one of them by name, and the dataset reuses their IRIs,
// so it is comparable with REF straight away. A column with no match becomes
// a term of the institution's own, as when starting from a file without one.
//
// Pure. The vocabulary comes from the institution's own catalog (one it holds,
// such as a reused pack) or from a partner's published catalog.json; either way
// it is cut down to one namespace with what its terms need.

import { ownVocabularies } from './vocabulary_files.js';
import { schemaOf } from './schema_label.js';

const prefixOf = (curie) => String(curie).split(':')[0];
const localOf = (curie) => String(curie).slice(String(curie).indexOf(':') + 1);
const norm = (text) => String(text ?? '').trim().toLowerCase();
const loose = (text) => norm(text).replace(/[^a-z0-9]+/g, '');

// One namespace of a catalog, with the value lists and sections its terms use
// and the prefixes to read it with: what describing a file with it needs, and
// what is added to our catalog when it is reused. Null when the namespace has
// no record class with terms.
export function vocabularyOf(catalog, prefix, { publisher = '' } = {}) {
  const inPrefix = (key) => (catalog?.[key] ?? []).filter((entry) => prefixOf(entry.id) === prefix);
  const terms = inPrefix('terms');
  const recordClasses = inPrefix('recordClasses').filter((rc) => terms.some((t) => [t.record].flat().includes(rc.id)));
  if (!recordClasses.length) return null;
  const used = new Set(terms.map((t) => t.valueScheme).filter(Boolean));
  const sections = new Set(terms.map((t) => t.section).filter(Boolean));
  const prefixes = Object.fromEntries(Object.entries(catalog.prefixes ?? {}).filter(([p]) => p === prefix));
  return {
    prefix, namespace: catalog.prefixes?.[prefix] ?? null, prefixes, recordClasses, terms,
    schema: schemaOf(catalog, recordClasses[0].id, { publisher }),
    valueSchemes: (catalog.valueSchemes ?? []).filter((s) => used.has(s.id)),
    sections: (catalog.sections ?? []).filter((s) => sections.has(s.id)),
  };
}

// The terms of one record class, whatever namespaces they come from, with
// what they need: what a file added to a dataset as another source is
// described with (step 7.4). Same shape as vocabularyOf's.
export function datasetVocabulary(catalog, recordClass) {
  const terms = (catalog?.terms ?? []).filter((t) => [t.record].flat().includes(recordClass));
  const used = new Set(terms.map((t) => t.valueScheme).filter(Boolean));
  const sections = new Set(terms.map((t) => t.section).filter(Boolean));
  return {
    prefix: prefixOf(recordClass), namespace: catalog?.prefixes?.[prefixOf(recordClass)] ?? null, prefixes: catalog?.prefixes ?? {},
    recordClasses: (catalog?.recordClasses ?? []).filter((rc) => rc.id === recordClass), terms,
    valueSchemes: (catalog?.valueSchemes ?? []).filter((s) => used.has(s.id)),
    sections: (catalog?.sections ?? []).filter((s) => sections.has(s.id)),
  };
}

// The vocabularies the institution holds that a file could be described
// with: every namespace with a record class and terms, core aside, ours first.
export function heldVocabularies(catalog, { publisher = '' } = {}) {
  const own = ownVocabularies(catalog ?? {}).map((v) => v.prefix);
  const prefixes = [...new Set((catalog?.recordClasses ?? []).map((rc) => prefixOf(rc.id)))]
    .filter((p) => p !== 'core').sort((a, b) => (own.includes(b) - own.includes(a)) || a.localeCompare(b));
  return prefixes.map((prefix) => vocabularyOf(catalog, prefix, { publisher })).filter(Boolean)
    .map((v) => ({ ...v, own: own.includes(v.prefix) }));
}

// The term a column names: its notation (the column a term was made for),
// then its local name, then its label or wording. Housekeeping columns (the
// key, the timestamp) and columns already claimed by an earlier one get none.
export function matchTerm(column, vocabulary, taken = new Set()) {
  const free = vocabulary.terms.filter((t) => !taken.has(t.id));
  const name = norm(column.name), squashed = loose(column.name);
  return free.find((t) => t.notation && norm(t.notation) === name)?.id
    ?? free.find((t) => loose(localOf(t.id)) === squashed)?.id
    ?? free.find((t) => [t.label, t.means].some((text) => text && loose(text) === squashed))?.id
    ?? null;
}

export function matchColumns(columns, vocabulary) {
  const taken = new Set();
  return columns.map((column) => {
    if (['key', 'date'].includes(column.kind)) return null;
    const id = matchTerm(column, vocabulary, taken);
    if (id) taken.add(id);
    return id;
  });
}

// How a column's values read as a term's: each value seen in the file to a
// concept of the term's value list, by code (an alias), label or the
// concept's own name; for a typed list (a scale), the value itself. `values`
// are the column's distinct values. Returns the map for the mapping and the
// values that fit nowhere, which are left out when the file is read in.
export function valueMatch(values, term, vocabulary) {
  const scheme = vocabulary.valueSchemes.find((s) => s.id === term.valueScheme);
  if (!scheme) return { map: null, unmatched: [], scheme: null };
  const concepts = scheme.concepts ?? (scheme.members ?? []).map((id) => ({ id }));
  const typed = scheme.output !== 'iri';
  const map = {}, unmatched = [];
  for (const value of values) {
    const hit = typed
      ? concepts.find((c) => String(c.id) === String(value).trim())
      : concepts.find((c) => [...(c.aliases ?? []), c.label, localOf(c.id)].some((text) => text != null && norm(text) === norm(value)))
        ?? concepts.find((c) => [...(c.aliases ?? []), c.label, localOf(c.id)].some((text) => text != null && loose(text) === loose(value)));
    if (hit) map[value] = String(hit.id);
    else unmatched.push(value);
  }
  // A later file may hold a value nobody chose this time: every concept is
  // reachable by its code, as a hand-made mapping lists them.
  for (const c of concepts) {
    const code = typed ? String(c.id) : (c.aliases ?? [])[0];
    if (code != null && !(code in map) && !Object.values(map).includes(String(c.id))) map[code] = String(c.id);
  }
  return { map, unmatched, scheme };
}

// The mapping transform that reads a column as a reused term. A term that is
// never shared (free text) is not read at all.
export function reusedTransform(term, vocabulary, values, delimiter = null) {
  if (term.posture?.treatment === 'drop') return null;
  const { map, scheme } = valueMatch(values, term, vocabulary);
  if (!scheme) return { fn: 'direct' };
  const toValue = scheme.output === 'iri'
    ? { fn: 'mapValues', output: 'iri', default: 'drop', map }
    : { fn: 'mapValues', output: 'typed', datatype: scheme.datatype ?? 'xsd:string', default: 'drop', map };
  return delimiter ? [{ fn: 'split', delimiter }, toValue] : toValue;
}

// What reusing a vocabulary adds to our catalog: the namespace and the record
// class, terms, value lists and sections of it that we do not hold yet, as
// they are, marked as coming from it.
export function reusedEntries(catalog, vocabulary, termIds) {
  const want = new Set(termIds);
  const terms = vocabulary.terms.filter((t) => want.has(t.id));
  const schemes = new Set(terms.map((t) => t.valueScheme).filter(Boolean));
  const sections = new Set(terms.map((t) => t.section).filter(Boolean));
  const records = new Set(terms.flatMap((t) => [t.record].flat()));
  const missing = (key, list) => {
    const held = new Set((catalog?.[key] ?? []).map((entry) => entry.id));
    return list.filter((entry) => !held.has(entry.id)).map((entry) => ({ ...entry, from: entry.from ?? vocabulary.prefix }));
  };
  return {
    prefixes: vocabulary.prefixes,
    recordClasses: missing('recordClasses', vocabulary.recordClasses.filter((rc) => records.has(rc.id))),
    terms: missing('terms', terms),
    valueSchemes: missing('valueSchemes', vocabulary.valueSchemes.filter((s) => schemes.has(s.id))),
    sections: missing('sections', vocabulary.sections.filter((s) => sections.has(s.id))),
  };
}
